import { supabase } from '@/integrations/supabase/client';

export interface MonitoredUrl {
  id: string;
  url: string;
  last_schemas: any[] | null;
  last_check_at: string | null;
  next_check_at: string;
  status: string;
  alert_on_change: boolean;
  alert_on_error: boolean;
  created_at: string;
}

export interface MonitoringAlert {
  id: string;
  monitored_url_id: string;
  alert_type: string;
  message: string;
  details: any | null;
  is_read: boolean;
  created_at: string;
}

export interface SchemaChangeHistoryItem {
  id: string;
  monitored_url_id: string;
  change_type: string;
  summary: string;
  previous_schemas: any[] | null;
  current_schemas: any[] | null;
  details: any | null;
  created_at: string;
}

export const monitoringApi = {
  async getMonitoredUrls(): Promise<MonitoredUrl[]> {
    const { data, error } = await supabase
      .from('monitored_urls')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching monitored URLs:', error);
      return [];
    }

    return (data || []) as MonitoredUrl[];
  },

  async addMonitoredUrl(url: string): Promise<{ success: boolean; error?: string }> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('monitored_urls')
      .insert({
        user_id: userData.user.id,
        url,
      });

    if (error) {
      console.error('Error adding monitored URL:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  async removeMonitoredUrl(id: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase
      .from('monitored_urls')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error removing monitored URL:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  async getAlerts(unreadOnly = false): Promise<MonitoringAlert[]> {
    // Limit history to 18 months
    const eighteenMonthsAgo = new Date();
    eighteenMonthsAgo.setMonth(eighteenMonthsAgo.getMonth() - 18);

    let query = supabase
      .from('monitoring_alerts')
      .select('*')
      .gte('created_at', eighteenMonthsAgo.toISOString())
      .order('created_at', { ascending: false });

    if (unreadOnly) {
      query = query.eq('is_read', false);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }

    return (data || []) as MonitoringAlert[];
  },

  async getAllAlerts(): Promise<MonitoringAlert[]> {
    // Get all alerts (read + unread) limited to 18 months
    const eighteenMonthsAgo = new Date();
    eighteenMonthsAgo.setMonth(eighteenMonthsAgo.getMonth() - 18);

    const { data, error } = await supabase
      .from('monitoring_alerts')
      .select('*, monitored_urls(url)')
      .gte('created_at', eighteenMonthsAgo.toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching all alerts:', error);
      return [];
    }

    return (data || []) as MonitoringAlert[];
  },

  async markAlertRead(id: string): Promise<void> {
    await supabase
      .from('monitoring_alerts')
      .update({ is_read: true })
      .eq('id', id);
  },

  async getChangeHistory(monitoredUrlId: string): Promise<SchemaChangeHistoryItem[]> {
    const eighteenMonthsAgo = new Date();
    eighteenMonthsAgo.setMonth(eighteenMonthsAgo.getMonth() - 18);

    const { data, error } = await supabase
      .from('schema_change_history')
      .select('*')
      .eq('monitored_url_id', monitoredUrlId)
      .gte('created_at', eighteenMonthsAgo.toISOString())
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching change history:', error);
      return [];
    }

    return (data || []) as SchemaChangeHistoryItem[];
  },
};
